import { retrieveRouteComponentsFromPath } from "./retrieve-route-components-from-path";
import { RouteElement } from "./RouteElement";
import type { RouteConfig, ShouldShowContext } from "./types";
import type { FeatureName } from "../permissions";
import { Navigate, Route } from "react-router-dom";

export type RouteElementArgs = RouteConfig & {
  path: string;
};

export interface GetRouteComponentOptions {
  shouldShowContext: ShouldShowContext | null;
  isFeatureEnabled: (featureName: FeatureName) => boolean;
}

/** This looks like a component, but must be a function since react-router-dom requires Router children to be their exact component */
export const getRouteComponent = (
  { path, ...config }: RouteElementArgs,
  { shouldShowContext, isFeatureEnabled }: GetRouteComponentOptions
) => {
  const subRoutes = config.subRoutes ? Object.entries(config.subRoutes) : [];
  const { pageComponent, useDefaultTitle, indexRedirect } = retrieveRouteComponentsFromPath(path) ?? {};

  if (config.featureName && !isFeatureEnabled(config.featureName)) return null;

  if (config.shouldShow !== undefined) {
    if (typeof config.shouldShow === "function") {
      if (shouldShowContext && !config.shouldShow(shouldShowContext)) return null;
    } else if (!config.shouldShow) {
      return null;
    }
  }

  if (config.adminOnly && shouldShowContext && !shouldShowContext.isAdmin) return null;

  if (config.modelForAccess && shouldShowContext) {
    if (shouldShowContext.session.userInfo.access[config.modelForAccess]?.showInSidebar === false) return null;
    if (!shouldShowContext.session.userInfo.access[config.modelForAccess]?.read) return null;
  }

  const indexElement =
    typeof indexRedirect === "string" ? (
      <Navigate replace to={indexRedirect} />
    ) : indexRedirect ? (
      <RouteElement
        isProtected={config.isProtected}
        hideMainLayout={!!config.hideMainLayout}
        useDefaultTitle={useDefaultTitle}
      >
        {indexRedirect}
      </RouteElement>
    ) : null;

  return (
    <Route
      key={path}
      path={path}
      element={
        pageComponent ? (
          <RouteElement
            isProtected={config.isProtected}
            hideMainLayout={!!config.hideMainLayout}
            useDefaultTitle={useDefaultTitle}
          >
            {pageComponent}
          </RouteElement>
        ) : undefined
      }
    >
      {indexElement && <Route index element={indexElement} />}
      {subRoutes.map(([subRoutePath, subConfig]) =>
        getRouteComponent(
          {
            path: `${path}${subRoutePath}`,
            ...subConfig,
          },
          { shouldShowContext, isFeatureEnabled }
        )
      )}
    </Route>
  );
};
